import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  ButtonGroup,
  Typography,
  Tooltip,
  FormControl,
  Select,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { clearDevicesList } from "slices/devicesSlice";
import { setDeviceFilters } from "slices/filtersSlice";
import { clearDevicesPagination } from "slices/paginationSlice";

import { useTheme } from "@emotion/react";

const DevicesContent = (rest) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const deviceFilters = useSelector(
    (state) => state.filtersState.deviceFilters
  );

  const [btnStyleAssembly, setBtnStyleAssembly] = useState(0);
  const [btnStyleCalibration, setBtnStyleCalibration] = useState(0);

  const [deviceFilterState, setDeviceFilterState] = useState({
    assemblyStatus: null,
    calibrationStatus: null,
    startDate: null,
    endDate: null,
    deviceType: null,
  });

  const handleChangeDeviceType = (event) => {
    let deviceType = event.target.value;
    setDeviceFilterState({
      ...deviceFilterState,
      deviceType: deviceType,
    });
  };

  const handleChange = (event) => {
    event.persist();

    setDeviceFilterState((prevFilteredState) => ({
      ...prevFilteredState,
      [event.target.name]: event.target.value,
    }));
  };

  useEffect(() => {
    if (rest.openMobile && rest.onMobileClose) {
      rest.onMobileClose();
    }

    // eslint-disable-next-line
  }, []); //??
  useEffect(() => {
    dispatch(clearDevicesList());
    dispatch(clearDevicesPagination());
    dispatch(
      setDeviceFilters({
        ...deviceFilters,
        assemblyStatus: deviceFilterState.assemblyStatus,
        calibrationStatus: deviceFilterState.calibrationStatus,
        startDate: deviceFilterState.startDate,
        endDate: deviceFilterState.endDate,
        deviceType:
          deviceFilterState.deviceType === "ALL"
            ? null
            : deviceFilterState.deviceType,
      })
    );
  }, [
    dispatch,
    setDeviceFilters,
    clearDevicesList,
    clearDevicesPagination,
    deviceFilterState,
  ]);

  const handleChangeAssembly = (result) => {
    if (result === "ALL") {
      result = null;
      setBtnStyleAssembly(0);
    } else if (result === "SUCCESS") {
      result = 1;
      setBtnStyleAssembly(1);
    } else {
      result = 0;
      setBtnStyleAssembly(2);
    }

    setDeviceFilterState({
      ...deviceFilterState,
      assemblyStatus: result,
    });
  };
  const handleChangeCalibration = (result) => {
    if (result === "ALL") {
      result = null;
      setBtnStyleCalibration(0);
    } else if (result === "SUCCESS") {
      result = 1;
      setBtnStyleCalibration(1);
    } else {
      setBtnStyleCalibration(2);
      result = 0;
    }
    setDeviceFilterState({
      ...deviceFilterState,
      calibrationStatus: result,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        "& > *": {
          margin: theme.spacing(1),
        },
      }}
    >
      <Typography variant="h6" gutterBottom style={{ margin: "10px 0" }}>
        <br />
        Last PCBA Test Status
      </Typography>
      <ButtonGroup
        variant="contained"
        size="small"
        aria-label="outlined small button group"
      >
        <Button
          children="ALL"
          color={btnStyleAssembly === 0 ? "primary" : ""}
          onClick={() => {
            handleChangeAssembly("ALL");
          }}
        />
        <Button
          children="SUCCESS"
          color={btnStyleAssembly === 1 ? "primary" : ""}
          onClick={() => {
            handleChangeAssembly("SUCCESS");
          }}
        />
        <Button
          children="FAILURE"
          color={btnStyleAssembly === 2 ? "primary" : ""}
          onClick={() => {
            handleChangeAssembly("FAILURE");
          }}
        />
      </ButtonGroup>
      <Typography variant="h6" gutterBottom style={{ margin: "10px 0" }}>
        <br />
        Last Calibration Status
      </Typography>
      <ButtonGroup
        size="small"
        variant="contained"
        aria-label="outlined small button group"
      >
        <Button
          children="ALL"
          color={btnStyleCalibration === 0 ? "primary" : ""}
          onClick={() => {
            handleChangeCalibration("ALL");
          }}
        />
        <Button
          children="SUCCESS"
          color={btnStyleCalibration === 1 ? "primary" : ""}
          onClick={() => {
            handleChangeCalibration("SUCCESS");
          }}
        />
        <Button
          children="FAILURE"
          color={btnStyleCalibration === 2 ? "primary" : ""}
          onClick={() => {
            handleChangeCalibration("FAILURE");
          }}
        />
      </ButtonGroup>

      <Typography variant="h6" gutterBottom style={{ margin: "0px 0" }}>
        <br />
        Date
        <Tooltip
          arrow
          title={
            "Filters devices by the most recent calibration test date. " +
            "If no calibration test is available, the devices are " +
            "filtered by their last modification time."
          }
        >
          <IconButton size="large">
            <InfoOutlined />
          </IconButton>
        </Tooltip>
      </Typography>

      <TextField
        id="date"
        label="From"
        variant="outlined"
        type="date"
        name="startDate"
        sx={{
          marginTop: "10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          marginTop: theme.spacing(3),
          width: 150,
        }}
        onChange={handleChange}
        defaultValue="2018-05-05"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <TextField
        id="date"
        label="To"
        name="endDate"
        variant="outlined"
        type="date"
        onChange={handleChange}
        defaultValue={`${rest.today}`}
        sx={{ marginTop: theme.spacing(3), width: 150 }}
        InputLabelProps={{
          shrink: true,
        }}
        style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}
      />
      <Typography
        variant="h6"
        gutterBottom
        style={{ margin: "10px 0", marginTop: "15px" }}
      >
        <br />
        Product
      </Typography>
      <FormControl
        variant="outlined"
        style={{
          backgroundColor: "#f5f5f5",
          minWidth: 150,
          maxWidth: 200,
        }}
      >
        <Select
          value={deviceFilterState.deviceType ?? "ALL"}
          onChange={handleChangeDeviceType}
          inputProps={{
            name: "deviceType",
          }}
        >
          <MenuItem value="ALL">ALL</MenuItem>
          <MenuItem value="SPIROHOME">SPIROHOME PERSONAL</MenuItem>
          <MenuItem value="SPIROCLINIC">SPIROHOME CLINIC</MenuItem>
          <MenuItem value="SPIROCLINIC_COMPACT">SPIROCLINIC COMPACT</MenuItem>
          <MenuItem value="SPIROCLINIC_PRO_HANDPIECE">
            SPIROCLINIC PRO HANDPIECE
          </MenuItem>
          <MenuItem value="SPIROCLINIC_PRO_DOCK">SPIROCLINIC PRO DOCK</MenuItem>
          <MenuItem value="ISPIRO">ISPIRO ULTRASONIC</MenuItem>
          <MenuItem value="ISPIRO_PRO">ISPIRO PRO</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export default DevicesContent;
